import { HomeContainer } from '@/components/common'
import { Button } from '@/components/ui/button'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { sanitize } from '@/lib/helpers/sanitize'
import { normalizeSlug, removeFileExtension } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'

type Props = {
  title: string
  imageUrl: string
  imageMobileUrl: string
  buttonText: string
  description: string
  href: string
  linkTarget: '_blank' | '_self'
  priority?: boolean
  isMobile?: boolean
  index?: number
  homeType?: 'nam' | 'nu' | 'chung'
}

export const CollectionSection: React.FC<Props> = ({
  title,
  imageUrl,
  imageMobileUrl,
  href,
  buttonText,
  description,
  linkTarget,
  priority = false,
  isMobile = false,
  index = 1,
  homeType,
}) => {
  const itm_medium = `homepage_banner_section${index ?? ''}`
  const itm_campaign = isMobile ? imageMobileUrl : imageUrl
  const itm_content = isMobile ? imageMobileUrl : imageUrl
  const url = `/${normalizeSlug(href)}?itm_source=homepage&itm_medium=${itm_medium}&itm_campaign=${removeFileExtension(itm_campaign)}&itm_content=${removeFileExtension(itm_content)}`

  // Format tracking value based on homeType
  const trackingValue =
    homeType === 'nam'
      ? `home-nam__section-banner__vitri-${index + 1}`
      : homeType === 'nu'
        ? `home-nu__section-banner__vitri-${index + 1}`
        : `home__section-banner__vitri-${index + 1}`

  return (
    <HomeContainer className='px-0 lg:px-0'>
      <figure className='relative'>
        <picture className='relative aspect-square w-full md:aspect-[19/6]'>
          <source
            media='(max-width: 768px)'
            srcSet={ImageHelper.getImageUrl(imageMobileUrl, '1024_1092')}
            sizes='(max-width: 768px) 100vw, 585px'
          />
          <Image
            src={ImageHelper.getImageUrl(imageUrl)}
            alt={`${title} - Collection banner image`}
            width={1920}
            height={600}
            className='w-full object-cover max-lg:max-h-[540px]'
            loading={priority ? 'eager' : 'lazy'}
            draggable={false}
            sizes='(max-width: 768px) 100vw, 1920px'
            {...(priority ? { priority: true, fetchPriority: 'high' } : {})}
          />
          {/* <div className='absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent md:w-[54%] md:bg-gradient-to-r' /> */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent md:hidden' />
          {/* <div className='absolute inset-0 bg-[linear-gradient(261.17deg,rgba(0,0,0,0)_47.21%,rgba(0,0,0,0.5)_100.33%)]' /> */}
        </picture>
        <figcaption
          className='absolute bottom-6 left-0 px-4 text-light lg:px-15 xl:bottom-17.5'
          // role='region'
          aria-label={`Collection section: ${title}`}
        >
          <h2
            className='text-heading-1 lg:mb-3'
            dangerouslySetInnerHTML={{ __html: sanitize(title) }}
          />
          <p
            className='text-body-1 mb-5 lg:mb-9'
            dangerouslySetInnerHTML={{ __html: sanitize(description) }}
          ></p>
          <Button
            asChild
            variant='white'
            className='lg:h-12.5 lg:px-12 lg:py-3'
            aria-label={`View ${title} collection`}
          >
            <Link
              href={url}
              aria-label={`${buttonText} - ${title} collection`}
              target={linkTarget}
              ga-tracking-value={trackingValue}
              ga-tracking-label={buttonText}
              rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
              prefetch={false}
              title={`${buttonText} - ${title} collection`}
            >
              {buttonText}
            </Link>
          </Button>
        </figcaption>
      </figure>
    </HomeContainer>
  )
}
